import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { OverlayTrigger, Popover, CloseButton } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useBottomScrollListener } from "react-bottom-scroll-listener";

import { contains, getStockCompanyIcon, debounce } from "utils";

import { Avatar } from "components/Avatar";
import { Loader } from "components/Loader";
import { useNavigation } from "utils/navigate";
import { InputSearch } from "components/Form";
import defaultStrategyImage from "assets/icons/Button utility.png";
import { useMarketplaceStrategies } from "features/Explore/hook/useMarketplaceStrategies";
import { SearchContainer, SearchPopover } from "../Navbar.styles";
import { isLoadingStockSelect, stockDataSelect } from "Layout/slice/selector";
import TabsSwitcher from "features/Strategy/v2/ETFBuilder/Components/CompanyDetails/TabSwitcher";

const TabOptions = [
  { key: "strategies", name: "Strategies" },
  { key: "stock", name: "Stocks" },
];

const PAGE_SIZE = 20;

export const Search = (props) => {
  const stockData = useSelector(stockDataSelect);
  const isLoadingStock = useSelector(isLoadingStockSelect);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState("strategies");
  const [filteredData, setFilteredData] = useState([]);
  const [show, setShow] = useState(false);

  const {
    strategies,
    isLoading: isLoadingStrategies,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMarketplaceStrategies({
    search: activeTab === "strategies" ? searchValue : "",
    category: "equity",
    size: PAGE_SIZE,
  });

  const { navigateToV2StockDetails, navigateToV2MarketplaceDetail } =
    useNavigation();

  const scrollRef = useBottomScrollListener(
    () => {
      if (activeTab === "strategies" && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    {
      offset: 300,
    }
  );

  useEffect(() => {
    setFilteredData(stockData?.slice(0, 100));
  }, [stockData]);

  const getFilterSymbol = (value = searchValue) => {
    if (!value) {
      setFilteredData(stockData?.slice(0, 100));
      return;
    }
    const filteredSymbolData = stockData
      .filter((asset) => {
        const val = value.toLowerCase();
        const companyName = asset[0].toLowerCase();
        const symbol = asset[1].toLowerCase();

        return contains(symbol, val) || contains(companyName, val);
      })
      .slice(0, 10);
    setFilteredData(filteredSymbolData);
  };

  const handleTabClick = (filter) => {
    inputRef.current?.focus();
    setActiveTab(filter.key);
    setSearchValue("");
    getFilterSymbol();
  };

  const handleStockClick = (symbol) => {
    navigateToV2StockDetails(symbol);
    setSearchValue("");
    document.body.click();
  };

  const handleStategyClick = (id) => {
    navigateToV2MarketplaceDetail(id);
    document.body.click();
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchValue(value);
      }, 600),
    []
  );

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchValue(value);

    if (activeTab === "stock") {
      getFilterSymbol(value);
      return;
    }

    debouncedSearch(value);
  };

  const clearSearch = () => {
    setSearchValue("");

    if (activeTab === "stock") {
      getFilterSymbol();
    }
  };

  const handleClose = () => {
    document.body.click();
  };

  const handleToggle = (value) => {
    if (!value && !searchValue) {
      setShow(false);
      return;
    }

    setShow(true);
  };

  return (
    <SearchContainer>
      <OverlayTrigger
        rootClose
        trigger={["click"]}
        placement="bottom-start"
        arrowOffsetLeft={100}
        show={show}
        onToggle={handleToggle}
        overlay={
          <SearchPopover ref={scrollRef}>
            <Popover.Body ref={wrapperRef}>
              <div className="tab-category-wrapper">
                <TabsSwitcher
                  tabs={TabOptions.map((t) => ({ key: t.key, label: t.name }))}
                  activeTab={activeTab}
                  onChange={(key) => handleTabClick({ key })}
                  variant="underline"
                />

                <div className="mobile-view pt-0">
                  <CloseButton onClick={handleClose} />
                </div>
              </div>

              {activeTab === "stock" && (
                <div className="search-list">
                  {!isLoadingStock &&
                    filteredData.map((symbol, inx) => (
                      <div
                        className="search-item"
                        onClick={() => handleStockClick(symbol[1])}
                        key={inx}
                      >
                        <div className="img-box">
                          <Avatar
                            src={getStockCompanyIcon(symbol[1])}
                            name={symbol[1]}
                            size="small"
                          />
                        </div>
                        <div
                          className="symbol-text"
                          title={`${symbol[1]} - ${symbol[0]}`}
                        >
                          <div className="symbol-title">{symbol[1]}</div>
                          <div className="symbol-desc">{symbol[0]}</div>
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {activeTab === "strategies" && (
                <div className="search-list">
                  {!isLoadingStrategies &&
                    strategies.map((strategy, inx) => (
                      <div
                        className="search-item strategy-search-item"
                        onClick={() => handleStategyClick(strategy.strategy_id)}
                        key={inx}
                      >
                        <img
                          className="strategy-thumb"
                          src={strategy.strategy_image || defaultStrategyImage}
                          alt={strategy.name}
                        />
                        <div className="strategy-text">{strategy.name}</div>
                      </div>
                    ))}
                </div>
              )}

              {(isLoadingStock || isLoadingStrategies) && (
                <div className="my-4">
                  <Loader />
                </div>
              )}

              <div className="more-data-loading">
                {isFetchingNextPage ? "Loading..." : ""}
              </div>
            </Popover.Body>
          </SearchPopover>
        }
      >
        <div>
          <InputSearch
            id={`${props.id}-search`}
            placeholder="Find an investment"
            ref={inputRef}
            value={searchValue}
            handleChange={handleSearch}
            clearSearch={clearSearch}
          />
        </div>
      </OverlayTrigger>
    </SearchContainer>
  );
};

Search.propTypes = {
  id: PropTypes.string,
};
